import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { vendasAPI, produtosAPI, empresasAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

const TIPOS_VENDA = ['Residencial', 'Empresarial'];
const FORMAS_PAGAMENTO = ['Boleto', 'Cartão de Crédito', 'Débito Automático', 'Pix'];
const VENCIMENTOS = ['5', '10', '15', '20', '25'];

const EditarVenda = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [venda, setVenda] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  
  const [formData, setFormData] = useState({
    telefone_venda: '',
    cliente: {
      tipo_documento: 'CPF',
      documento: '',
      nome_razao_social: '',
      email: '',
      telefone1: '',
      telefone2: '',
      nome_mae: '',
      rg: '',
      data_nascimento: ''
    },
    endereco: {
      cep: '',
      endereco: '',
      numero: '',
      complemento: '',
      cidade: '',
      estado: '',
      hp: ''
    },
    produtos: [],
    tipo_venda: 'Residencial',
    valor_plano: '',
    empresa_id: '',
    pagamento: {
      forma_pagamento: 'Boleto',
      vencimento_fatura: '10'
    },
    observacoes_campo: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vendaRes, produtosRes, empresasRes] = await Promise.all([
        vendasAPI.get(id),
        produtosAPI.list(),
        empresasAPI.list()
      ]);
      
      const vendaData = vendaRes.data;
      setVenda(vendaData);
      setProdutos(produtosRes.data);
      setEmpresas(empresasRes.data);
      
      // Verificar se a venda pode ser editada (status Devolvida)
      if (vendaData.status !== 'Devolvida') {
        toast.error('Esta venda não pode ser editada. Status deve ser "Devolvida".');
        navigate(`/vendas/${id}`);
        return;
      }
      
      // Verificar se o usuário tem permissão para editar
      // Admin e BKO podem editar qualquer venda devolvida
      // Vendedor só pode editar suas próprias vendas
      if (user?.perfil === 'VENDEDOR' && vendaData.vendedor_id !== user?.id) {
        toast.error('Você só pode editar suas próprias vendas.');
        navigate(`/vendas/${id}`);
        return;
      }
      
      // Preencher formulário com dados da venda
      setFormData({
        telefone_venda: vendaData.telefone_venda || '',
        cliente: {
          tipo_documento: vendaData.cliente?.tipo_documento || 'CPF',
          documento: vendaData.cliente?.documento || '',
          nome_razao_social: vendaData.cliente?.nome_razao_social || '',
          email: vendaData.cliente?.email || '',
          telefone1: vendaData.cliente?.telefone1 || '',
          telefone2: vendaData.cliente?.telefone2 || '',
          nome_mae: vendaData.cliente?.nome_mae || '',
          rg: vendaData.cliente?.rg || '',
          data_nascimento: vendaData.cliente?.data_nascimento || ''
        },
        endereco: {
          cep: vendaData.endereco?.cep || '',
          endereco: vendaData.endereco?.endereco || '',
          numero: vendaData.endereco?.numero || '',
          complemento: vendaData.endereco?.complemento || '',
          cidade: vendaData.endereco?.cidade || '',
          estado: vendaData.endereco?.estado || '',
          hp: vendaData.endereco?.hp || ''
        },
        produtos: vendaData.produtos || [],
        tipo_venda: vendaData.tipo_venda || 'Residencial',
        valor_plano: vendaData.valor_plano?.toString() || '',
        empresa_id: vendaData.empresa_id || '',
        pagamento: {
          forma_pagamento: vendaData.pagamento?.forma_pagamento || 'Boleto',
          vencimento_fatura: vendaData.pagamento?.vencimento_fatura || '10'
        },
        observacoes_campo: vendaData.observacoes_campo || ''
      });
    } catch (error) {
      console.error('Erro ao carregar venda:', error);
      toast.error('Erro ao carregar venda');
      navigate('/acompanhamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      // Preparar dados para envio
      const dadosAtualizados = {
        ...formData,
        valor_plano: parseFloat(formData.valor_plano) || 0,
        status: 'Enviado ao BKO' // Envia para o BKO após correção
      };
      
      // Usar endpoint de correção que permite vendedor
      await vendasAPI.corrigir(id, dadosAtualizados);
      toast.success('Venda corrigida e enviada ao BKO com sucesso!');
      navigate(`/vendas/${id}`);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar alterações');
    } finally {
      setSalvando(false);
    }
  };

  // Agrupar produtos por categoria
  const produtosPorCategoria = produtos.reduce((acc, produto) => {
    if (produto.ativo) {
      const cat = produto.categoria || 'Outros';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(produto);
    }
    return acc;
  }, {});

  const handleProdutoToggle = (produtoId) => {
    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.includes(produtoId)
        ? prev.produtos.filter(id => id !== produtoId)
        : [...prev.produtos, produtoId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/vendas/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Editar Venda</h1>
            <p className="text-gray-500">Protocolo: {venda?.protocolo}</p>
          </div>
        </div>
      </div>

      {/* Aviso */}
      <Card className="bg-yellow-50 border-yellow-200">
        <CardContent className="pt-4">
          <p className="text-yellow-800">
            ⚠️ Esta venda foi <strong>devolvida</strong> e pode ser corrigida. 
            Após salvar, o status será alterado para <strong>"Enviado ao BKO"</strong>.
          </p>
        </CardContent>
      </Card>

      {/* Dados do Cliente */}
      <Card>
        <CardHeader>
          <CardTitle>Dados do Cliente</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Tipo de Documento</Label>
              <Select
                value={formData.cliente.tipo_documento}
                onValueChange={(value) => setFormData({
                  ...formData,
                  cliente: {...formData.cliente, tipo_documento: value}
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CPF">CPF</SelectItem>
                  <SelectItem value="CNPJ">CNPJ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{formData.cliente.tipo_documento} *</Label>
              <Input
                value={formData.cliente.documento}
                onChange={(e) => setFormData({
                  ...formData,
                  cliente: {...formData.cliente, documento: e.target.value}
                })}
                required
              />
            </div>

            <div>
              <Label>Nome/Razão Social *</Label>
              <Input
                value={formData.cliente.nome_razao_social}
                onChange={(e) => setFormData({
                  ...formData,
                  cliente: {...formData.cliente, nome_razao_social: e.target.value}
                })}
                required
              />
            </div>

            <div>
              <Label>E-mail *</Label>
              <Input
                type="email"
                value={formData.cliente.email}
                onChange={(e) => setFormData({
                  ...formData,
                  cliente: {...formData.cliente, email: e.target.value}
                })}
                required
              />
            </div>

            <div>
              <Label>Telefone 1 *</Label>
              <Input
                value={formData.cliente.telefone1}
                onChange={(e) => setFormData({
                  ...formData,
                  cliente: {...formData.cliente, telefone1: e.target.value}
                })}
                required
              />
            </div>

            <div>
              <Label>Telefone 2</Label>
              <Input
                value={formData.cliente.telefone2}
                onChange={(e) => setFormData({
                  ...formData,
                  cliente: {...formData.cliente, telefone2: e.target.value}
                })}
              />
            </div>

            {formData.cliente.tipo_documento === 'CPF' && (
              <>
                <div>
                  <Label>Nome da Mãe</Label>
                  <Input
                    value={formData.cliente.nome_mae}
                    onChange={(e) => setFormData({
                      ...formData,
                      cliente: {...formData.cliente, nome_mae: e.target.value}
                    })}
                  />
                </div>

                <div>
                  <Label>RG</Label>
                  <Input
                    value={formData.cliente.rg}
                    onChange={(e) => setFormData({
                      ...formData,
                      cliente: {...formData.cliente, rg: e.target.value}
                    })}
                  />
                </div>

                <div>
                  <Label>Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={formData.cliente.data_nascimento}
                    onChange={(e) => setFormData({
                      ...formData,
                      cliente: {...formData.cliente, data_nascimento: e.target.value}
                    })}
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Endereço */}
      <Card>
        <CardHeader>
          <CardTitle>Endereço</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>CEP *</Label>
              <Input
                value={formData.endereco.cep}
                onChange={(e) => setFormData({
                  ...formData,
                  endereco: {...formData.endereco, cep: e.target.value}
                })}
                required
              />
            </div>

            <div className="md:col-span-2">
              <Label>Endereço *</Label>
              <Input
                value={formData.endereco.endereco}
                onChange={(e) => setFormData({
                  ...formData,
                  endereco: {...formData.endereco, endereco: e.target.value}
                })}
                required
              />
            </div>

            <div>
              <Label>Número *</Label>
              <Input
                value={formData.endereco.numero}
                onChange={(e) => setFormData({
                  ...formData,
                  endereco: {...formData.endereco, numero: e.target.value}
                })}
                required
              />
            </div>

            <div>
              <Label>Complemento</Label>
              <Input
                value={formData.endereco.complemento}
                onChange={(e) => setFormData({
                  ...formData,
                  endereco: {...formData.endereco, complemento: e.target.value}
                })}
              />
            </div>

            <div>
              <Label>Cidade *</Label>
              <Input
                value={formData.endereco.cidade}
                onChange={(e) => setFormData({
                  ...formData,
                  endereco: {...formData.endereco, cidade: e.target.value}
                })}
                required
              />
            </div>

            <div>
              <Label>Estado *</Label>
              <Input
                value={formData.endereco.estado}
                onChange={(e) => setFormData({
                  ...formData,
                  endereco: {...formData.endereco, estado: e.target.value}
                })}
                maxLength={2}
                required
              />
            </div>

            <div>
              <Label>HP</Label>
              <Input
                value={formData.endereco.hp}
                onChange={(e) => setFormData({
                  ...formData,
                  endereco: {...formData.endereco, hp: e.target.value}
                })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Produtos */}
      <Card>
        <CardHeader>
          <CardTitle>Produtos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(produtosPorCategoria).map(([categoria, prods]) => (
              <div key={categoria}>
                <h4 className="font-semibold text-gray-700 mb-2">{categoria}</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {prods.map((produto) => (
                    <label
                      key={produto.id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.produtos.includes(produto.id)
                          ? 'bg-red-50 border-red-500'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.produtos.includes(produto.id)}
                        onChange={() => handleProdutoToggle(produto.id)}
                        className="mr-2"
                      />
                      <span className="text-sm">{produto.nome}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dados da Venda */}
      <Card>
        <CardHeader>
          <CardTitle>Dados da Venda</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Tipo de Venda *</Label>
              <Select
                value={formData.tipo_venda}
                onValueChange={(value) => setFormData({ ...formData, tipo_venda: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_VENDA.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Valor do Plano (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.valor_plano}
                onChange={(e) => setFormData({ ...formData, valor_plano: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Empresa</Label>
              <Select
                value={formData.empresa_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, empresa_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {empresas.filter(e => e.ativo !== false).map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>{empresa.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Telefone da Venda</Label>
              <Input
                value={formData.telefone_venda}
                onChange={(e) => setFormData({ ...formData, telefone_venda: e.target.value })}
              />
            </div>

            <div>
              <Label>Forma de Pagamento</Label>
              <Select
                value={formData.pagamento.forma_pagamento}
                onValueChange={(value) => setFormData({
                  ...formData,
                  pagamento: {...formData.pagamento, forma_pagamento: value}
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMAS_PAGAMENTO.map((forma) => (
                    <SelectItem key={forma} value={forma}>{forma}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Vencimento da Fatura</Label>
              <Select
                value={formData.pagamento.vencimento_fatura}
                onValueChange={(value) => setFormData({
                  ...formData,
                  pagamento: {...formData.pagamento, vencimento_fatura: value}
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VENCIMENTOS.map((dia) => (
                    <SelectItem key={dia} value={dia}>Dia {dia}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <Label>Observações</Label>
            <Textarea
              value={formData.observacoes_campo}
              onChange={(e) => setFormData({ ...formData, observacoes_campo: e.target.value })}
              rows={3}
              placeholder="Observações sobre a venda..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Botões de Ação */}
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={() => navigate(`/vendas/${id}`)}>
          Cancelar
        </Button>
        <Button onClick={handleSalvar} disabled={salvando}>
          {salvando ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Salvar Alterações
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EditarVenda;
